import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { http, fileUrl } from "@/api";
import { useStore } from "@/context/StoreContext";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, Panel, SingleImage, SortableList } from "./ui";
import { useSettingsDraft } from "./settingsDraft";

const HERO_DEFAULTS = [
  "https://customer-assets-4nw71qhi.emergentagent.net/job_7d5f4f3e-3f3c-4221-a662-e0903fa95238/artifacts/caabc467b879e3cb_Gemini_Generated_Image_h8jrl6h8jrl6h8jr%20%281%29.webp",
  "https://customer-assets-4nw71qhi.emergentagent.net/job_7d5f4f3e-3f3c-4221-a662-e0903fa95238/artifacts/c205cc2cbb109a6c_Gemini_Generated_Image_jbjnzmjbjnzmjbjn.webp",
  "https://customer-assets-4nw71qhi.emergentagent.net/job_7d5f4f3e-3f3c-4221-a662-e0903fa95238/artifacts/239e06e393098a4f_Gemini_Generated_Image_d87z9d87z9d87z9d.webp",
];
const PROJECTS_DEFAULT = "https://images.unsplash.com/photo-1525441273400-056e9c7517b3?auto=format&fit=crop&w=1200&q=80";

const F = ({ label, children }) => <div><Label>{label}</Label>{children}</div>;

function ChangePassword() {
  const { setToken } = useStore();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  async function submit(e) {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("Las contraseñas nuevas no coinciden");
    setBusy(true);
    try {
      const { data } = await http.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      if (data.token) setToken(data.token);
      toast.success("Contraseña actualizada. Se cerraron las demás sesiones abiertas.");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar la contraseña");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Panel title="Cambiar contraseña de administrador">
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl" data-testid="change-password-form">
        <F label="Contraseña actual"><Input type="password" autoComplete="current-password" value={f.current_password} onChange={(e) => set("current_password", e.target.value)} data-testid="pw-current" /></F>
        <F label="Nueva contraseña"><Input type="password" autoComplete="new-password" value={f.new_password} onChange={(e) => set("new_password", e.target.value)} data-testid="pw-new" /></F>
        <F label="Repite la nueva contraseña"><Input type="password" autoComplete="new-password" value={f.confirm} onChange={(e) => set("confirm", e.target.value)} data-testid="pw-confirm" /></F>
        <p className="sm:col-span-3 text-xs text-neutral-500">Mínimo 8 caracteres, con letras y números. Tras 5 intentos fallidos de ingreso, el acceso se bloquea 15 minutos.</p>
        <div className="sm:col-span-3"><Button type="submit" className="btn-primary" disabled={busy || !f.current_password || !f.new_password} data-testid="pw-submit">{busy ? "Guardando…" : "Cambiar contraseña"}</Button></div>
      </form>
    </Panel>
  );
}

export default function SettingsPage() {
  const { d, set, setIn, SaveBtn } = useSettingsDraft();
  if (!d) return null;
  const heroes = [0, 1, 2].map((i) => d.hero_images?.[i] || HERO_DEFAULTS[i]);
  const faq = d.faq || [];
  const updFaq = (i, k, v) => set("faq", faq.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div data-testid="admin-settings-page">
      <PageHeader title="Configuración general" subtitle="Los cambios se publican en la tienda al guardar" actions={SaveBtn} />
      <Tabs defaultValue="general">
        <TabsList className="bg-white border border-black/5 flex-wrap h-auto">
          <TabsTrigger value="general" data-testid="settings-tab-general">Negocio</TabsTrigger>
          <TabsTrigger value="texts" data-testid="settings-tab-texts">Textos y mensajes</TabsTrigger>
          <TabsTrigger value="images" data-testid="settings-tab-images">Banners y fotos</TabsTrigger>
          <TabsTrigger value="policies" data-testid="settings-tab-policies">Políticas y FAQ</TabsTrigger>
          <TabsTrigger value="colors" data-testid="settings-tab-colors">Colores</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="settings-tab-alerts">Alertas</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security">Seguridad</TabsTrigger>
        </TabsList>

        <TabsContent value="alerts" className="mt-6">
          <Panel title="Alertas por correo">
            <p className="text-sm text-neutral-500 mb-4">Te enviaremos un correo cada vez que llegue un pedido o una cotización nueva.</p>
            <div className="flex items-center gap-2 mb-4">
              <Switch checked={d.alerts_enabled !== false} onCheckedChange={(v) => set("alerts_enabled", v)} data-testid="s-alerts-enabled" />
              <span className="text-sm font-semibold">Alertas activadas</span>
            </div>
            <F label="Correo que recibe las alertas"><Input type="email" value={d.alert_email || ""} onChange={(e) => set("alert_email", e.target.value)} data-testid="s-alert-email" /></F>
          </Panel>
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <ChangePassword />
        </TabsContent>

        <TabsContent value="general" className="mt-6">
          <Panel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <F label="Nombre comercial"><Input value={d.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="s-business-name" /></F>
              <F label="Número de WhatsApp"><Input value={d.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} data-testid="s-whatsapp" /></F>
              <F label="Dirección"><Input value={d.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="s-address" /></F>
              <F label="Horarios de atención"><Input value={d.hours || ""} onChange={(e) => set("hours", e.target.value)} data-testid="s-hours" /></F>
              <F label="Instagram"><Input value={d.social?.instagram || ""} onChange={(e) => setIn("social", "instagram", e.target.value)} data-testid="s-instagram" /></F>
              <F label="Facebook"><Input value={d.social?.facebook || ""} onChange={(e) => setIn("social", "facebook", e.target.value)} /></F>
              <F label="TikTok"><Input value={d.social?.tiktok || ""} onChange={(e) => setIn("social", "tiktok", e.target.value)} /></F>
              <F label="Logotipo"><SingleImage value={d.logo_url} onChange={(v) => set("logo_url", v)} testid="s-logo-upload" /></F>
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="texts" className="mt-6 space-y-6">
          <Panel title="Banner principal">
            <div className="grid grid-cols-1 gap-4">
              <F label="Título"><Input value={d.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} data-testid="s-hero-title" /></F>
              <F label="Subtítulo"><Textarea rows={2} value={d.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} data-testid="s-hero-subtitle" /></F>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <F label="Botón principal"><Input value={d.hero_cta_primary || ""} onChange={(e) => set("hero_cta_primary", e.target.value)} /></F>
                <F label="Botón secundario"><Input value={d.hero_cta_secondary || ""} onChange={(e) => set("hero_cta_secondary", e.target.value)} /></F>
              </div>
              <F label="Texto de la sección de proyectos"><Textarea rows={2} value={d.home_message_2 || ""} onChange={(e) => set("home_message_2", e.target.value)} /></F>
            </div>
          </Panel>
          <Panel title="Mensajes de confirmación">
            <div className="grid grid-cols-1 gap-4">
              <F label="Al generar un pedido"><Textarea rows={2} value={d.msg_order_confirmation || ""} onChange={(e) => set("msg_order_confirmation", e.target.value)} data-testid="s-msg-order" /></F>
              <F label="Al enviar una cotización"><Textarea rows={2} value={d.msg_quote_confirmation || ""} onChange={(e) => set("msg_quote_confirmation", e.target.value)} data-testid="s-msg-quote" /></F>
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="images" className="mt-6 space-y-6">
          <Panel title="Fotos del banner principal">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {heroes.map((src, i) => (
                <div key={i}>
                  <Label>{["Foto grande", "Foto pequeña superior", "Foto pequeña inferior"][i]}</Label>
                  <img src={fileUrl(src)} alt="" className="w-full aspect-[4/3] object-cover rounded-xl border my-2" />
                  <SingleImage value={d.hero_images?.[i] || ""} onChange={(v) => { const a = [...heroes]; a[i] = v || HERO_DEFAULTS[i]; set("hero_images", a); }} testid={`s-hero-image-${i}`} />
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="Foto de la sección de proyectos">
            <img src={fileUrl(d.projects_image) || PROJECTS_DEFAULT} alt="" className="w-full max-w-md aspect-[4/3] object-cover rounded-xl border mb-2" />
            <SingleImage value={d.projects_image || ""} onChange={(v) => set("projects_image", v)} testid="s-projects-image" />
            <p className="text-xs text-neutral-500 mt-3">Las fotos de los recuadros de categorías se cambian en la sección Categorías.</p>
          </Panel>
        </TabsContent>

        <TabsContent value="policies" className="mt-6 space-y-6">
          <Panel title="Políticas">
            <div className="grid grid-cols-1 gap-4">
              <F label="Políticas de compra"><Textarea rows={5} value={d.policies_purchase || ""} onChange={(e) => set("policies_purchase", e.target.value)} data-testid="s-policies-purchase" /></F>
              <F label="Condiciones de cambios y devoluciones"><Textarea rows={5} value={d.policies_returns || ""} onChange={(e) => set("policies_returns", e.target.value)} data-testid="s-policies-returns" /></F>
            </div>
          </Panel>
          <Panel title="Preguntas frecuentes">
            <SortableList items={faq.map((x, i) => ({ ...x, _k: `f${i}-${x.q?.length || 0}` }))} getId={(x) => x._k} onReorder={(l) => set("faq", l.map(({ _k, ...x }) => x))}
              renderItem={(x, i) => (
                <div className="bg-neutral-50 rounded-xl p-3 space-y-2" data-testid={`faq-${i}`}>
                  <div className="flex gap-2">
                    <Input placeholder="Pregunta" value={x.q || ""} onChange={(e) => updFaq(i, "q", e.target.value)} />
                    <Button variant="outline" size="icon" onClick={() => set("faq", faq.filter((_, j) => j !== i))}><Trash2 size={14} /></Button>
                  </div>
                  <Textarea rows={2} placeholder="Respuesta" value={x.a || ""} onChange={(e) => updFaq(i, "a", e.target.value)} />
                </div>
              )} />
            <Button size="sm" variant="outline" className="mt-3" onClick={() => set("faq", [...faq, { q: "", a: "" }])} data-testid="add-faq"><Plus size={14} className="mr-1" /> Agregar pregunta</Button>
          </Panel>
        </TabsContent>

        <TabsContent value="colors" className="mt-6">
          <Panel title="Colores de marca">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[["primary", "Principal (botones)"], ["accent", "Acento (etiquetas)"], ["background", "Fondo"], ["surface", "Tarjetas"], ["text", "Texto"]].map(([k, l]) => (
                <div key={k}>
                  <Label>{l}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={d.brand_colors?.[k] || "#000000"} onChange={(e) => setIn("brand_colors", k, e.target.value)} className="h-10 w-10 rounded cursor-pointer" data-testid={`s-color-${k}`} />
                    <Input value={d.brand_colors?.[k] || ""} onChange={(e) => setIn("brand_colors", k, e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
